import { useEffect, useRef, useState, useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { useGetCaseProfileScreeningRecordsWithMatchesQuery } from "~/graphql/supergraphHooks";
import { type GetCaseProfileScreeningRecordsWithMatchesQuery } from "~/graphql/supergraphTypes";
import { useEnableAiScreening } from "~/pages/Compliance/flags";
import { ScreeningRecord } from "../../types";

interface UseScreeningPollingProps {
  caseId: number;
  programId?: number;
  profileId?: string;
}

export type ScreeningPollingResult = {
  data: GetCaseProfileScreeningRecordsWithMatchesQuery;
  loading: boolean;
  error: ApolloError | undefined;
  isPolling: boolean;
  pollingAttempts: number;
  riskAssessmentFailed: boolean;
};

export const useScreeningPolling = ({
  caseId,
  programId,
  profileId,
}: UseScreeningPollingProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [riskAssessmentFailed, setRiskAssessmentFailed] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_ATTEMPTS = 6;
  const POLLING_INTERVAL = 10000; // 10 seconds
  const TOTAL_TIMEOUT = 60000; // 60 seconds

  const enableAiScreening = useEnableAiScreening();

  const { data, loading, error, refetch } = useGetCaseProfileScreeningRecordsWithMatchesQuery({
    variables: {
      caseId,
      programId,
    },
    fetchPolicy: "network-only",
  });

  // Check if the current profile's matches have null riskAssessment
  const hasNullRiskAssessment = useCallback((): boolean => {
    if (!data?.complianceCase || !profileId) return false;

    const profileIdNum = parseInt(profileId, 10);
    // Handle invalid profileId
    if (isNaN(profileIdNum)) return false;

    const checkMatches = (screeningRecord: ScreeningRecord | null | undefined): boolean => {
      if (!screeningRecord?.matches) return false;
      return screeningRecord.matches.some((match) => match.riskAssessment === null);
    };

    // Check if it's the investing profile
    if (data.complianceCase.investingProfile?.id === profileIdNum) {
      // json scalar is unknown type, cast to any as we don't validate the structure
      return checkMatches(data.complianceCase.investingProfile.screeningRecord as any);
    }

    // Check if it's one of the other profiles
    const targetProfile = data.complianceCase.profiles?.find(
      (profile) => profile?.id === profileIdNum,
    );
    if (targetProfile?.screeningRecord) {
      // json scalar is unknown type, cast to any as we don't validate the structure
      return checkMatches(targetProfile.screeningRecord as any);
    }

    // Profile not found or no screening record
    return false;
  }, [data, profileId]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current || timeoutRef.current) return;

    setIsPolling(true);
    setPollingAttempts(0);
    setRiskAssessmentFailed(false);

    // Set timeout to stop polling after 60 seconds
    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setRiskAssessmentFailed(true);
    }, TOTAL_TIMEOUT);

    // Start interval for polling
    intervalRef.current = setInterval(() => {
      setPollingAttempts((prev) => {
        const newAttempts = prev + 1;
        if (newAttempts >= MAX_ATTEMPTS) {
          stopPolling();
          setRiskAssessmentFailed(true);
          return prev;
        }
        refetch().catch(() => {
          stopPolling();
          setRiskAssessmentFailed(true);
        });
        return newAttempts;
      });
    }, POLLING_INTERVAL);
  }, [refetch, stopPolling]);

  // Effect to start/stop polling based on data
  useEffect(() => {
    if (loading || error || !enableAiScreening) {
      stopPolling();
      setRiskAssessmentFailed(false);
      return;
    }

    if (data && hasNullRiskAssessment()) {
      startPolling();
    } else {
      stopPolling();
      setRiskAssessmentFailed(false);
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [
    data,
    loading,
    error,
    enableAiScreening,
    hasNullRiskAssessment,
    startPolling,
    stopPolling,
  ]);

  return {
    data,
    loading,
    error,
    isPolling,
    pollingAttempts,
    riskAssessmentFailed,
  };
};
